import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Tag,
  Tooltip,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { packageGroupService } from '../../services/packageGroupService';
import { PackageGroupVO, PackageGroupRequest } from '../../types';
import DeviceTypeSelector from '../../components/DeviceTypeSelector';
import DealerSelector from '../../components/DealerSelector';

const { Option } = Select;
const { TextArea } = Input;

const PackageGroupList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PackageGroupVO[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PackageGroupVO | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const { user } = useAuth();

  // 状态选项
  const statusOptions = [
    { label: '启用', value: 1 },
    { label: '禁用', value: 0 },
  ];

  // 套餐组类型选项
  const groupTypeOptions = [
    { label: '充值套餐', value: 'PAY' },
    { label: '免费套餐', value: 'FREE' },
  ];

  // 获取套餐组列表
  const fetchData = async (page = current, size = pageSize, searchParams?: any) => {
    setLoading(true);
    try {
      const params = {
        page: page - 1, // 后端从0开始
        size,
        ...searchParams,
      };
      
      const response = await packageGroupService.queryPackageGroups(params);
      console.log('response:', response);
      setData(response.content);
      setTotal(response.totalElements || 0);
      setCurrent(page);
      setPageSize(size);
    } catch (error) {
      message.error('获取套餐组列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 搜索
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    fetchData(1, pageSize, values);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    fetchData(1, pageSize);
  };

  // 新增/编辑
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      status: 1,
      dealerId: user?.dealerId,
      groupType: 'PAY',
    });
    setModalVisible(true);
  };

  const handleEdit = (record: PackageGroupVO) => {
    setEditingRecord(record);
    form.setFieldsValue({
      groupName: record.groupName,
      groupType: record.groupType,
      deviceTypeId: record.deviceTypeId,
      dealerId: record.dealerId,
      status: record.status,
      description: record.description,
      remark: record.remark,
    });
    setModalVisible(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const createBy = user?.realName || user?.username || 'admin';
      
      if (editingRecord) {
        await packageGroupService.updatePackageGroupWithDto(editingRecord.id, values, createBy);
        message.success('套餐组更新成功');
      } else {
        await packageGroupService.createPackageGroupWithDto(values, createBy);
        message.success('套餐组创建成功');
      }
      
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 删除
  const handleDelete = async (record: PackageGroupVO) => {
    try {
      const updateBy = user?.realName || user?.username || 'admin';
      await packageGroupService.deletePackageGroup(record.id, updateBy);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 启用/禁用
  const handleToggleStatus = async (record: PackageGroupVO) => {
    try {
      const updateBy = user?.realName || user?.username || 'admin';
      if (record.status === 1) {
        await packageGroupService.disablePackageGroup(record.id, updateBy);
        message.success('已禁用');
      } else {
        await packageGroupService.enablePackageGroup(record.id, updateBy);
        message.success('已启用');
      }
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '套餐组名称',
      dataIndex: 'groupName',
      key: 'groupName',
      width: 150,
    },
    {
      title: '套餐组类型',
      dataIndex: 'groupType',
      key: 'groupType',
      width: 100,
      render: (value: string) => {
        const option = groupTypeOptions.find(opt => opt.value === value);
        return (
          <Tag color={value === 'PAY' ? 'blue' : 'green'}>
            {option?.label || value}
          </Tag>
        );
      },
    },
    {
      title: '设备类型',
      dataIndex: 'deviceTypeName',
      key: 'deviceTypeName',
      width: 100,
    },
    {
      title: '经销商',
      dataIndex: 'dealerName',
      key: 'dealerName',
      width: 120,
      render: (value: string, record: PackageGroupVO) => {
        return record.dealerId ? value : '公共套餐组';
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (value: number) => (
        <Tag color={value === 1 ? 'green' : 'red'}>
          {value === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '套餐总数量/启用数量',
      dataIndex: 'packageCount',
      key: 'packageCount',
      width: 150,
      render: (value: number, record: PackageGroupVO) => (
        <span>
          {value} / <span style={{ color: 'green' }}>{record.activePackageCount || 0}</span>
        </span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: PackageGroupVO) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 1 ? '禁用' : '启用'}>
            <Button
              type="link"
              onClick={() => handleToggleStatus(record)}
            >
              {record.status === 1 ? '禁用' : '启用'}
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个套餐组吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="套餐组管理" extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增套餐组
          </Button>
        </Space>
      }>
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          style={{ marginBottom: 16 }}
          onFinish={handleSearch}
        >
          <Form.Item name="groupName" label="套餐组名称">
            <Input placeholder="请输入套餐组名称" allowClear />
          </Form.Item>
          
          <Form.Item name="deviceTypeId" label="设备类型">
            <DeviceTypeSelector style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="请选择状态" allowClear style={{ width: 100 }}>
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, size) => fetchData(page, size),
            onShowSizeChange: (current, size) => fetchData(1, size),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑套餐组' : '新增套餐组'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 1,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="groupName"
                label="套餐组名称"
                rules={[
                  { required: true, message: '请输入套餐组名称' },
                  { max: 64, message: '套餐组名称不能超过64个字符' },
                ]}
              >
                <Input placeholder="请输入套餐组名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="groupType"
                label="套餐组类型"
                rules={[{ required: true, message: '请选择套餐组类型' }]}
              >
                <Select placeholder="请选择套餐组类型">
                  {groupTypeOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="deviceTypeId"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <DeviceTypeSelector style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dealerId"
                label="经销商ID"
              >
               <DealerSelector style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            rules={[{ max: 255, message: '描述不能超过255个字符' }]}
          >
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>
          <Form.Item
            name="remark"
            label="备注"
            rules={[{ max: 255, message: '备注不能超过255个字符' }]}
          >
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PackageGroupList; 